import { useEffect, useRef } from "react";
import { useEditorStore } from "../store";

export interface UseAutosaveOptions {
  onSave?: (doc: ReturnType<typeof useEditorStore.getState>["document"]) => Promise<void>;
  debounceMs?: number;
}

export function useAutosave({ onSave, debounceMs = 1500 }: UseAutosaveOptions = {}) {
  const isDirty = useEditorStore((s) => s.isDirty);
  const document = useEditorStore((s) => s.document);
  const setSaveStatus = useEditorStore((s) => s.setSaveStatus);
  const markSaved = useEditorStore((s) => s.markSaved);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isDirty) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        if (onSave) {
          await onSave(document);
        }
        markSaved();
      } catch (err) {
        console.error("Autosave failed:", err);
        setSaveStatus("error");
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isDirty, document, debounceMs, onSave, setSaveStatus, markSaved]);

  // Warn before browser window or tab closes with unsaved changes (PRD §146)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (useEditorStore.getState().isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
}
